'use client';

import { putPreferenceRoute } from '@/app/api/preferences/route.def';
import { callApi } from '@/domains/common/api-client';
import type { UserPreferences } from '@/external/db/types';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type FunctionComponent,
  type ReactNode,
} from 'react';

export type PreferenceKey = keyof UserPreferences;

export const DEFAULT_PREFERENCES: UserPreferences = {
  auto_confirm: false,
  show_tool_logs: false,
  tour_completed: false,
  tour_dismissed_steps: [],
};

interface PreferencesState {
  values: UserPreferences;
  // Latest value read imperatively — for send-time reads (e.g. auto_confirm)
  // that must not capture a stale render.
  getPreference: <K extends PreferenceKey>(key: K) => UserPreferences[K];
  setPreference: <K extends PreferenceKey>(key: K, value: UserPreferences[K]) => void;
}

const PreferencesContext = createContext<PreferencesState>({
  values: DEFAULT_PREFERENCES,
  getPreference: key => DEFAULT_PREFERENCES[key],
  setPreference: () => undefined,
});

export const UserPreferencesProvider: FunctionComponent<{
  initial: Partial<UserPreferences>;
  children: ReactNode;
}> = ({ initial, children }) => {
  const [values, setValues] = useState<UserPreferences>(() => ({ ...DEFAULT_PREFERENCES, ...initial }));
  const valuesRef = useRef(values);
  valuesRef.current = values;

  const getPreference = useCallback(<K extends PreferenceKey>(key: K) => valuesRef.current[key], []);
  const setPreference = useCallback(<K extends PreferenceKey>(key: K, value: UserPreferences[K]) => {
    setValues(prev => ({ ...prev, [key]: value }));
    void callApi(putPreferenceRoute, { body: { key, value }, gracefulFallback: () => null });
  }, []);

  const state = useMemo(
    () => ({ values, getPreference, setPreference }),
    [values, getPreference, setPreference],
  );
  return <PreferencesContext.Provider value={state}>{children}</PreferencesContext.Provider>;
};

export function useUserPreferences(): PreferencesState {
  return useContext(PreferencesContext);
}

export function usePreference<K extends PreferenceKey>(
  key: K,
): [UserPreferences[K], (value: UserPreferences[K]) => void] {
  const { values, setPreference } = useContext(PreferencesContext);
  return [values[key], value => setPreference(key, value)];
}
